import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isTomorrow } from 'date-fns'
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { Loader2 } from 'lucide-react'
import { useProjects } from '../hooks/useProjects'
import { useUpcomingDayFormat as useDayFormat } from '../hooks/useUpcomingDayFormat'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import {
  LATER_SECTION,
  OVERDUE_SECTION,
  groupUpcoming,
  hasChosenTime,
  isRange,
  shortDayLabel,
  upcomingSectionOf,
} from '../utils/groupUpcoming'
import { TaskList } from './TaskList'
import { UpcomingWeekBoard } from './UpcomingWeekBoard'
import type { UpcomingDay } from '../utils/groupUpcoming'
import type { UpcomingDayFormat as DayFormat } from '../hooks/useUpcomingDayFormat'
import type { TaskReorderEvent } from './TaskList'
import type { TaskViewContext } from './TaskPageLayout'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { UpcomingLayout } from '@/store/ViewModeStore'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'
import { createDragExtractor } from '@/lib/utils/dragDrop'
import { cn } from '@/lib/utils'

type UpcomingTaskListProps = TaskViewContext & {
  onReorder?: (event: TaskReorderEvent) => void
  layout?: UpcomingLayout
}

const extractTaskDragData = createDragExtractor('task')

const sectionAnchor = (id: string) => `upcoming-${id}`

/**
 * Next 7 days (design 34a): a week strip for load at a glance, overdue on
 * top, every day present with a one-line header, a project chip per row, and
 * a Week layout (34b, UpcomingWeekBoard).
 */
export function UpcomingTaskList({
  items,
  isLoading,
  defaultOnReorder,
  onReorder,
  onSelect,
  selectedTaskId,
  containerClassName,
  containerStyle,
  itemClassName,
  viewMode,
  layout = 'list',
}: UpcomingTaskListProps) {
  const { t } = useTranslation('tasks')
  const f = useDayFormat()
  const { projects } = useProjects()
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects])
  const groups = useMemo(() => groupUpcoming(items), [items])
  const reorder = onReorder ?? defaultOnReorder
  const scrollRef = useRef<HTMLDivElement>(null)

  // TaskList only knows its own rows, so each day's list stamps where the
  // drag came from and where it landed.
  const reorderInto = useCallback(
    (targetSectionId: string) => (event: TaskReorderEvent) => {
      const source = items.find((item) => item.id === event.sourceId)
      reorder?.({
        ...event,
        sourceSectionId: source ? upcomingSectionOf(source, groups) : undefined,
        targetSectionId,
      })
    },
    [items, groups, reorder],
  )

  // One ID column across every day's list (13a).
  const idColumnWidthCh = useMemo(() => {
    const keyById = new Map(projects.map((p) => [p.id, p.projectKey]))
    const longest = items.reduce(
      (max, task) => Math.max(max, resolveTaskIdentifier(task, task.projectId ? keyById.get(task.projectId) : null).length),
      0,
    )
    return longest === 0 ? 0 : Math.max(7, longest)
  }, [items, projects])

  const formatTaskDue = useCallback(
    (task: TaskDocument) => {
      if (!task.dueDate) return ''
      if (groups.overdue.some((overdue) => overdue.id === task.id)) {
        // Overdue: the date it was due, or when a range ended (31b).
        const date = f.monthDay(task.dueDate)
        return isRange(task) ? t('upcoming.endedOn', { date }) : date
      }
      if (groups.later.some((later) => later.id === task.id)) {
        return t('upcoming.dueOn', { date: f.monthDay(task.dueDate) })
      }
      // Inside its day the header already says the date; only a chosen time.
      return hasChosenTime(task) ? f.time(task.dueDate) : ''
    },
    [groups, t, f],
  )

  const renderRowMeta = useCallback(
    (task: TaskDocument) => (
      <>
        {isRange(task) && task.startDate ? <RangeNote startDate={task.startDate} /> : null}
        <ProjectChip project={task.projectId ? projectById.get(task.projectId) : undefined} />
      </>
    ),
    [projectById],
  )

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[240px] items-center justify-center', containerClassName)}>
        <Loader2 className="size-5 animate-spin text-primary" />
      </div>
    )
  }

  const listProps = {
    itemClassName,
    onSelect,
    selectedTaskId,
    viewMode,
    virtualized: false,
    showEmptyState: false,
    renderRowMeta,
    formatTaskDue,
    idColumnWidthCh,
    className: 'min-w-0',
  } as const

  const jumpTo = (id: string) => {
    const target = scrollRef.current?.querySelector(`#${sectionAnchor(id)}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (layout === 'week') {
    return (
      <UpcomingWeekBoard
        groups={groups}
        className={containerClassName}
        style={containerStyle}
        onDropInto={(sectionId, event) => reorderInto(sectionId)(event)}
        viewMode={viewMode}
        onSelect={onSelect}
        selectedTaskId={selectedTaskId}
        formatTaskDue={formatTaskDue}
        projectById={projectById}
      />
    )
  }

  return (
    <div
      ref={scrollRef}
      className={cn('@container min-w-0 w-full overflow-y-auto pb-6', containerClassName)}
      style={containerStyle}
    >
      <WeekStrip days={groups.days} onJump={jumpTo} />
      {groups.overdue.length > 0 && (
        <section id={sectionAnchor(OVERDUE_SECTION)} className="scroll-mt-2">
          <DayHeader
            label={t('upcoming.overdue')}
            trailing={t('upcoming.taskCount', { count: groups.overdue.length })}
            tone="overdue"
          />
          <TaskList {...listProps} items={groups.overdue} onReorder={reorderInto(OVERDUE_SECTION)} />
        </section>
      )}
      {groups.days.map((day) => (
        <section key={day.key} id={sectionAnchor(day.key)} className="scroll-mt-2">
          {day.tasks.length === 0 ? (
            <EmptyDayRow day={day} onDrop={(event) => reorderInto(day.key)(event)} />
          ) : (
            <>
              <DayHeader label={dayName(day.date, t, f)} date={dayDate(day.date, f)} trailing={String(day.tasks.length)} />
              <TaskList {...listProps} items={day.tasks} onReorder={reorderInto(day.key)} />
            </>
          )}
        </section>
      ))}
      {groups.later.length > 0 && (
        <section id={sectionAnchor(LATER_SECTION)} className="scroll-mt-2">
          <DayHeader
            label={t('upcoming.endsLater')}
            date={t('upcoming.endsLaterHint', { date: f.weekdayMonthDay(groups.days[groups.days.length - 1]?.date ?? new Date()) })}
            trailing={String(groups.later.length)}
          />
          <TaskList {...listProps} items={groups.later} onReorder={reorderInto(LATER_SECTION)} />
        </section>
      )}
    </div>
  )
}

type TFn = ReturnType<typeof useTranslation<'tasks'>>['t']

/** "Tomorrow", else the weekday ("Tuesday"). */
function dayName(date: Date, t: TFn, f: DayFormat) {
  return isTomorrow(date) ? t('upcoming.tomorrow') : f.weekdayLong(date)
}

/** Tomorrow's header carries the weekday too: "Tomorrow Mon, Sep 28". */
function dayDate(date: Date, f: DayFormat) {
  return isTomorrow(date) ? f.weekdayMonthDay(date) : f.monthDay(date)
}

/** One 34px line per day (design 34a), not a 50px block with a big numeral. */
function DayHeader({
  label,
  date,
  trailing,
  tone,
  className,
}: {
  label: string
  date?: string
  trailing?: string
  tone?: 'overdue'
  className?: string
}) {
  return (
    <div className={cn('flex h-[34px] items-center gap-2 px-5 @2xl:px-6', className)}>
      <span className={cn('text-[13px] font-semibold', tone === 'overdue' ? 'text-brand' : 'text-foreground')}>
        {label}
      </span>
      {date && <span className="text-[13px] text-muted-foreground">{date}</span>}
      {trailing && <span className="ml-auto text-xs tabular-nums text-muted-foreground">{trailing}</span>}
    </div>
  )
}

/** A free day: one 30px row that still takes a dropped task (design 34a). */
function EmptyDayRow({ day, onDrop }: { day: UpcomingDay; onDrop: (event: TaskReorderEvent) => void }) {
  const { t } = useTranslation('tasks')
  const f = useDayFormat()
  const ref = useRef<HTMLDivElement>(null)
  const [isOver, setIsOver] = useState(false)
  const onDropRef = useRef(onDrop)
  onDropRef.current = onDrop

  useEffect(() => {
    const element = ref.current
    if (!element) return
    return dropTargetForElements({
      element,
      canDrop: ({ source }) => Boolean(extractTaskDragData(source.data)),
      getData: () => ({ type: 'upcoming-day', key: day.key }),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: ({ source }) => {
        setIsOver(false)
        const data = extractTaskDragData(source.data)
        if (data) onDropRef.current({ type: 'container', sourceId: data.id, dropPosition: 'end' })
      },
    })
  }, [day.key])

  return (
    <div
      ref={ref}
      aria-label={t('upcoming.dropOn', { day: f.weekdayLong(day.date) })}
      className={cn(
        'mx-3 flex h-[30px] items-center gap-2 rounded-md px-2 text-[13px] transition-colors @2xl:mx-4',
        isOver ? 'bg-brand-tint ring-1 ring-brand/40' : 'text-muted-foreground',
      )}
    >
      <span className="font-medium text-foreground/70">{f.weekdayLong(day.date)}</span>
      <span>{f.monthDay(day.date)}</span>
      <span className="ml-auto text-xs text-muted-foreground/70">{t('upcoming.nothingPlanned')}</span>
    </div>
  )
}

/** Load at a glance: a count per day, click to jump (design 34a). */
function WeekStrip({ days, onJump }: { days: Array<UpcomingDay>; onJump: (key: string) => void }) {
  const { t } = useTranslation('tasks')
  const f = useDayFormat()
  return (
    <div role="group" aria-label={t('upcoming.weekStrip')} className="mx-3 mb-2 mt-1 grid grid-cols-7 gap-1 @2xl:mx-4">
      {days.map((day) => {
        const count = day.tasks.length
        return (
          <button
            key={day.key}
            type="button"
            onClick={() => onJump(day.key)}
            aria-label={t('upcoming.jumpTo', { day: f.weekdayLongMonthDay(day.date) })}
            className={cn(
              'flex flex-col items-center gap-0.5 rounded-md border px-1 py-1.5 transition-colors hover:bg-secondary',
              count === 0 && 'border-dashed',
            )}
          >
            <span className="text-[11px] text-muted-foreground">
              {f.weekdayShort(day.date)} <span className="tabular-nums">{f.dayNum(day.date)}</span>
            </span>
            <span className={cn('text-[13px] font-semibold tabular-nums', count === 0 && 'text-muted-foreground/60')}>
              {count === 0 ? '–' : count}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** "Started Mon" / "Starts Tue" on a ranged row (31b → 34a). */
function RangeNote({ startDate }: { startDate: number }) {
  const { t } = useTranslation('tasks')
  const f = useDayFormat()
  const date = shortDayLabel(startDate, f)
  return (
    <span className="hidden shrink-0 text-[11.5px] text-muted-foreground @xl:inline">
      {startDate <= Date.now() ? t('upcoming.startedOn', { date }) : t('upcoming.startsOn', { date })}
    </span>
  )
}

/** Where the task is from: the project's mark and name, or Inbox (design 34a). */
function ProjectChip({ project }: { project?: ProjectDocument }) {
  const { t } = useTranslation('tasks')
  return (
    <span className="hidden max-w-[9rem] shrink-0 items-center gap-1.5 text-[11.5px] text-muted-foreground @lg:inline-flex">
      {project ? <LocationSwatch location={project} size={14} /> : null}
      <span className="truncate">{project ? project.name : t('upcoming.inbox')}</span>
    </span>
  )
}

export default UpcomingTaskList
